import { coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  HostBinding,
  inject,
  Input,
  TemplateRef,
} from '@angular/core'
import { CanSize, CanSizeCtor, mixinSize, Size } from '../size.mixin'

@Directive({
  // tslint:disable-next-line
  selector: 'button[appIconButton], a[appIconButton]',
})
export class IconButtonStylerDirective {
  @HostBinding('class.icon-button') buttonClass = true
}

/**
 * Get a color mixin that ButtonComponent can extend to handle color inputs
 */
class ButtonBase {
  _elementRef = inject<ElementRef>(ElementRef)
  constructor() {}
}
const _SizeMixinBase: CanSizeCtor & typeof ButtonBase = mixinSize(ButtonBase)

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'button[appButton], a[appButton]',
  styleUrls: ['./button.component.scss'],
  templateUrl: './button.component.html',
})
export class ButtonComponent extends _SizeMixinBase implements CanSize {
  // Style mode: Link
  @Input() set link(value: any) {
    this._link = coerceBooleanProperty(value)
  }
  get link() {
    return this._link
  }
  @HostBinding('class.link') _link = false

  // Style mode: Primary
  @Input() set primary(value: any) {
    this._primary = coerceBooleanProperty(value)
  }
  get primary() {
    return this._primary
  }
  @HostBinding('class.primary') _primary = false

  // Style mode: Accent
  @Input() set accent(value: any) {
    this._accent = coerceBooleanProperty(value)
  }
  get accent() {
    return this._accent
  }
  @HostBinding('class.accent') _accent = false

  // Style mode: Accent Strong
  @Input() set accentStrong(value: any) {
    this._accentStrong = coerceBooleanProperty(value)
  }
  get accentStrong() {
    return this._accentStrong
  }
  @HostBinding('class.accent-strong') _accentStrong = false

  // Style mode: Warn
  @Input() set warn(value: any) {
    this._warn = coerceBooleanProperty(value)
  }
  get warn() {
    return this._warn
  }
  @HostBinding('class.warn') _warn = false

  // Style mode: Body
  @Input() set body(value: any) {
    this._body = coerceBooleanProperty(value)
  }
  get body() {
    return this._body
  }
  @HostBinding('class.body') _body = false

  // Border
  @Input() set border(value: any) {
    this._border = coerceBooleanProperty(value)
  }
  get border() {
    return this._border
  }
  @HostBinding('class.border') _border = false

  // Translucent
  @Input() set translucent(value: any) {
    this._translucent = coerceBooleanProperty(value)
  }
  get translucent() {
    return this._translucent
  }
  @HostBinding('class.translucent') _translucent = false

  // Disabled
  @Input() set disabled(value: any) {
    this._disabled = coerceBooleanProperty(value)
  }
  get disabled() {
    return this._disabled
  }
  @HostBinding('class.disabled') _disabled = false

  // Stack (vertical list of buttons)
  @Input() set stack(value: any) {
    this._stack = coerceBooleanProperty(value)
  }
  get stack() {
    return this._stack
  }
  @HostBinding('class.stack') _stack = false

  // Inline (inline list of buttons)
  @Input() set inline(value: any) {
    this._inline = coerceBooleanProperty(value)
  }
  get inline() {
    return this._inline
  }
  @HostBinding('class.inline') _inline = false

  // Center
  @Input() set center(value: any) {
    this._center = coerceBooleanProperty(value)
  }
  get center() {
    return this._center
  }
  @HostBinding('class.center') _center = false

  // Extra Padding
  @Input() set extraPadding(value: any) {
    this._extraPadding = coerceBooleanProperty(value)
  }
  get extraPadding() {
    return this._extraPadding
  }
  @HostBinding('class.extra-padding') _extraPadding = false

  // Unclickable: No visible style/cursor changes on hover
  @Input() set unclickable(value: any) {
    this._unclickable = coerceBooleanProperty(value)
  }
  get unclickable() {
    return this._unclickable
  }
  @HostBinding('class.unclickable') _unclickable = false

  // Size mixin
  @Input() size: Size
  @Input() gigantic: boolean
  @Input() huge: boolean
  @Input() big: boolean
  @Input() small: boolean
  @Input() tiny: boolean
  @Input() mini: boolean
  @HostBinding('class.gigantic')
  get isSizeGigantic() {
    return this.size === 'gigantic'
  }
  @HostBinding('class.huge')
  get isSizeHuge() {
    return this.size === 'huge'
  }
  @HostBinding('class.big')
  get isSizeBig() {
    return this.size === 'big'
  }
  @HostBinding('class.small')
  get isSizeSmall() {
    return this.size === 'small'
  }
  @HostBinding('class.tiny')
  get isSizeTiny() {
    return this.size === 'tiny'
  }
  @HostBinding('class.mini')
  get isSizeMini() {
    return this.size === 'mini'
  }

  @ContentChild('rightTemplate') rightTemplate: TemplateRef<any>

  constructor() {
    super()
  }
}
